import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, X, Moon, Sun, ArrowRight, ArrowUpRight } from "lucide-react";
import { SERVICE_ID_TO_SLUG } from "@/data/serviceDetails";
import { NAV_LINKS, SERVICES } from "@/data/content";
import { useTheme } from "@/context/ThemeContext";

function Logo() {
  const { theme } = useTheme();
  return (
    <Link to="/" data-testid="nav-logo" className="flex items-center group">
      <div className={`transition-transform duration-300 group-hover:scale-105 ${theme === "dark" ? "rounded-lg bg-white px-3 py-1.5" : ""}`}>
        <img src="/assets/logo-240.webp" alt="Serpens Technology logo" width="116" height="32" fetchPriority="high" decoding="async" className="h-8 w-auto" />
      </div>
    </Link>
  );
}

function MegaMenu({ open }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="absolute left-1/2 top-full z-50 w-[min(92vw,880px)] -translate-x-1/2 pt-4"
          data-testid="services-mega-menu"
        >
          <div className="rounded-2xl border border-secondary/25 bg-card shadow-2xl shadow-black/15 p-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {SERVICES.map((s) => (
                <Link
                  key={s.id}
                  to={`/services/${SERVICE_ID_TO_SLUG[s.id]}`}
                  data-testid={`mega-menu-${s.id}`}
                  className="group flex items-start gap-3 rounded-xl p-3 transition-colors duration-200 hover:bg-muted/70"
                >
                  <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${s.flagship ? "bg-blue-800 text-white" : "bg-muted text-foreground"} transition-transform duration-300 group-hover:scale-110`}>
                    <s.icon className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                      {s.title}
                      {s.flagship && <span className="rounded-full bg-pink-500/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-pink-500">Flagship</span>}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground line-clamp-1">{s.tagline || s.subs.slice(0, 3).join(" · ")}</p>
                  </div>
                </Link>
              ))}
            </div>
            <div className="mt-6 flex items-center justify-between rounded-xl bg-muted/60 px-5 py-4">
              <p className="text-sm text-muted-foreground">Not sure what you need? We will map it in a free discovery call.</p>
              <Link to="/contact" data-testid="mega-menu-cta" className="flex items-center gap-1.5 text-sm font-semibold text-secondary hover:gap-2.5 transition-all duration-200">
                Talk to us <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function Navbar() {
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();

  return (
    <header className="sticky top-0 z-50 glass border-b border-border/50" data-testid="main-navbar">
      <nav className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 md:px-8">
        <Logo />

        <div className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((link) =>
            link.mega ? (
              <div
                key={link.path}
                className="relative"
                onMouseEnter={() => setMegaOpen(true)}
                onMouseLeave={() => setMegaOpen(false)}
              >
                <NavLink
                  to={link.path}
                  data-testid="nav-link-services"
                  className={({ isActive }) =>
                    `flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                      isActive ? "text-foreground bg-muted/70" : "text-muted-foreground hover:text-foreground"
                    }`
                  }
                >
                  {link.label}
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${megaOpen ? "rotate-180" : ""}`} />
                </NavLink>
                <MegaMenu open={megaOpen} />
              </div>
            ) : (
              <NavLink
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                    isActive ? "text-foreground bg-muted/70" : "text-muted-foreground hover:text-foreground"
                  }`
                }
              >
                {link.label}
              </NavLink>
            )
          )}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle"
            aria-label="Toggle theme"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-secondary/25 text-muted-foreground transition-colors duration-200 hover:text-foreground hover:border-border"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <Link
            to="/contact"
            data-testid="nav-cta-start-project"
            className="hidden sm:flex items-center gap-2 rounded-full bg-secondary px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-800/25 transition-all duration-300 hover:shadow-blue-800/40 hover:-translate-y-0.5 active:scale-95"
          >
            Start Your Project <ArrowUpRight className="h-4 w-4" />
          </Link>
          <button
            className="lg:hidden flex h-10 w-10 items-center justify-center rounded-full border border-secondary/25 text-foreground"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-button"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden border-t border-border/50 bg-card"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `rounded-xl px-4 py-3 text-base font-medium transition-colors ${
                      isActive ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                onClick={() => setMobileOpen(false)}
                data-testid="mobile-nav-cta"
                className="mt-3 flex items-center justify-center gap-2 rounded-full bg-secondary px-5 py-3 text-sm font-semibold text-white"
              >
                Start Your Project <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
